/**
 * Module Visibility Store - Gestión de visibilidad de módulos completos
 *
 * @version 1.0
 * @updated 2026-02-25
 * @module core/module-store
 *
 * @description
 * Store Zustand con persistencia para gestionar la visibilidad de módulos completos.
 * Cuando un módulo está oculto, TODAS sus capas se ocultan del mapa.
 *
 * @remarks
 * - Persistente en localStorage
 * - Sincroniza con ModuleSelector (long press → dropdown → eye button)
 * - Los paneles de sidebar escuchan cambios y ocultan/muestran elementos
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ModuleId } from './types';

/**
 * Estado del store
 */
export interface ModuleVisibilityState {
  /** Set de módulos actualmente ocultos */
  hiddenModules: Set<ModuleId>;
}

/**
 * Acciones del store
 */
export interface ModuleVisibilityActions {
  /** Oculta un módulo completo */
  hideModule: (moduleId: ModuleId) => void;
  /** Muestra un módulo completo */
  showModule: (moduleId: ModuleId) => void;
  /** Toggle visibilidad de un módulo */
  toggleModuleVisibility: (moduleId: ModuleId) => void;
  /** Verifica si un módulo está oculto */
  isModuleHidden: (moduleId: ModuleId) => boolean;
  /** Resetea todos los módulos a visible */
  showAllModules: () => void;
}

/**
 * Store completo
 */
export type ModuleVisibilityStore = ModuleVisibilityState & ModuleVisibilityActions;

/**
 * Hook del store con persistencia
 */
export const useModuleVisibilityStore = create<ModuleVisibilityStore>()(
  persist(
    (set, get) => ({
      // Estado inicial
      hiddenModules: new Set(),

      // Acciones
      hideModule: (moduleId: ModuleId) => {
        console.log('[ModuleVisibility] Ocultando módulo:', moduleId);
        set((state) => ({
          hiddenModules: new Set([...state.hiddenModules, moduleId]),
        }));

        // Emitir evento para que los paneles reaccionen
        window.dispatchEvent(
          new CustomEvent('module-visibility-changed', {
            detail: { moduleId, visible: false },
          })
        );
      },

      showModule: (moduleId: ModuleId) => {
        console.log('[ModuleVisibility] Mostrando módulo:', moduleId);
        set((state) => {
          const next = new Set(state.hiddenModules);
          next.delete(moduleId);
          return { hiddenModules: next };
        });

        // Emitir evento para que los paneles reaccionen
        window.dispatchEvent(
          new CustomEvent('module-visibility-changed', {
            detail: { moduleId, visible: true },
          })
        );
      },

      toggleModuleVisibility: (moduleId: ModuleId) => {
        const isHidden = get().isModuleHidden(moduleId);
        if (isHidden) {
          get().showModule(moduleId);
        } else {
          get().hideModule(moduleId);
        }
      },

      isModuleHidden: (moduleId: ModuleId) => {
        return get().hiddenModules.has(moduleId);
      },

      showAllModules: () => {
        console.log('[ModuleVisibility] Mostrando todos los módulos');
        set({ hiddenModules: new Set() });

        // Emitir evento global
        window.dispatchEvent(new CustomEvent('all-modules-visible'));
      },
    }),
    {
      name: 'wayline-module-visibility',
      // Serializar/deserializar Set correctamente
      partialize: (state) => ({
        hiddenModules: Array.from(state.hiddenModules),
      }),
      merge: (persistedState: any, currentState: ModuleVisibilityStore) => ({
        ...currentState,
        hiddenModules: new Set(persistedState?.hiddenModules || []),
      }),
    }
  )
);
